import { createDeferred, type Deferred } from './deferred'

/**
 * How long a startup operation waits for the native presentation to announce
 * itself before proceeding without it.
 *
 * `pageVisible` is delivered as soon as the WebView becomes visible — well
 * under a second on both platforms — so this bound is never reached by a real
 * presentation. It exists because there may be no presentation at all (a page
 * opened outside the native cover, or a delivery that is lost), and an
 * unbounded wait there is a deadlock, not a wait.
 */
const PAGE_VISIBLE_WAIT_MS = 2_000

let current: Deferred<void> = createDeferred<void>()

export const pageVisibleOnce = {
  get promise() {
    return current.promise
  },
  done: (v: void) => current.done(v),
  /**
   * Wait for the presentation announcement, but never forever.
   *
   * Resolves when `pageVisible` arrives, or when the bound elapses, and never
   * rejects: an operation that waited in vain simply proceeds with no page
   * budget, which is exactly how it behaved before recovery existed. Callers
   * that must not hang on a presentation that never speaks use this instead of
   * awaiting `promise` directly.
   */
  waitAtMost: (timeoutMs: number = PAGE_VISIBLE_WAIT_MS): Promise<void> => {
    const announced = current.promise
    return new Promise<void>((resolve) => {
      const timer = setTimeout(resolve, timeoutMs)
      void announced.then(() => {
        clearTimeout(timer)
        resolve()
      })
    })
  },
  /** @internal — used by __resetForTests in native.ts */
  __resetForTests(): void {
    current = createDeferred<void>()
  },
}
